/**
 * StatPeek.ts — Utility for formatting stat information inline with skill checks.
 *
 * Provides compact stat display for skill check prompts, showing:
 * - Stat abbreviation and value
 * - Calculated modifier (D&D style)
 * - DC requirement
 * - Color-coded success probability
 *
 * Note: Gallowcrest uses deterministic stat checks (no dice rolls), so
 * success is simply playerValue >= threshold. The color coding reflects
 * how close the player is to the threshold.
 */

import type { ColorName } from './ColorTheme';

export interface StatPeekOptions {
    /** Stat abbreviation (e.g., 'STR', 'DEX') */
    statAbbrev: string;
    /** Current stat value */
    statValue: number;
    /** Threshold for the check */
    threshold: number;
}

export interface StatPeekResult {
    /** Formatted text for display */
    text: string;
    /** Color based on pass/fail status */
    color: ColorName;
    /** Whether the check would pass */
    passes: boolean;
    /** Margin (positive = passes by this much, negative = fails by this much) */
    margin: number;
}

/**
 * Calculate the D&D-style modifier from a stat value.
 * Formula: floor((statValue - 10) / 2)
 */
export function calculateModifier(statValue: number): number {
    return Math.floor((statValue - 10) / 2);
}

/**
 * Get color based on pass/fail and margin.
 * - green: passes
 * - yellow: fails but close (within 2)
 * - red: fails by more than 2
 */
export function getCheckColor(passes: boolean, margin: number): ColorName {
    if (passes) return 'green';
    if (margin >= -2) return 'yellow';
    return 'red';
}

/**
 * Format a stat peek display for skill check choices.
 * Returns formatted text and color based on pass/fail status.
 * 
 * Format: "[ABBREV: value (±modifier)] vs threshold"
 */
export function formatStatPeek(options: StatPeekOptions): StatPeekResult {
    const { statAbbrev, statValue, threshold } = options;

    const modifier = calculateModifier(statValue);
    const passes = statValue >= threshold;
    const margin = statValue - threshold;
    const color = getCheckColor(passes, margin);

    // Format modifier with sign
    const modifierStr = modifier >= 0 ? `+${modifier}` : `${modifier}`;

    // Build the display text
    const text = `[${statAbbrev}: ${statValue} (${modifierStr})] vs ${threshold}`;

    return {
        text,
        color,
        passes,
        margin,
    };
}

/**
 * Format just the stat portion without threshold (for status bar display).
 * Format: "ABBREV: value (±modifier)"
 */
export function formatStatCompact(statAbbrev: string, statValue: number): string {
    const modifier = calculateModifier(statValue);
    const modifierStr = modifier >= 0 ? `+${modifier}` : `${modifier}`;
    return `${statAbbrev}: ${statValue} (${modifierStr})`;
}
